<script setup>
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  activeTab: {
    type: String,
    default: 'tasks',
  },
  onTabChange: {
    type: Function,
    default: null,
  },
  onOpenNavigation: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(['tabChange', 'openNavigation']);

const { t } = useI18n();

/**
 * Inline style reserving space for the iOS home indicator so the floating
 * pill never sits flush against the bottom bezel.
 * @type {{ paddingBottom: string }}
 */
const safeAreaPaddingStyle = {
  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
};

/**
 * Navigation tabs rendered inside the main glass pill. `icon` is the outline
 * glyph, `iconActive` its filled variant shown when the tab is active. `label`
 * is the visible caption key, `aria` the descriptive accessibility key. Search
 * is rendered separately as a detached round button on the right.
 * @type {Array<{ key: string, icon: string, iconActive: string, label: string, aria: string }>}
 */
const navTabs = [
  {
    key: 'communications',
    icon: 'i-ph-chat-circle',
    iconActive: 'i-ph-chat-circle-fill',
    label: 'MOBILE_TAB_BAR.CONVERSATIONS',
    aria: 'SIDEBAR.CONVERSATIONS',
  },
  {
    key: 'tasks',
    icon: 'i-ph-list-checks',
    iconActive: 'i-ph-list-checks-fill',
    label: 'MOBILE_TAB_BAR.TASKS',
    aria: 'TASKS.TITLE',
  },
  {
    key: 'contact',
    icon: 'i-ph-user',
    iconActive: 'i-ph-user-fill',
    label: 'MOBILE_TAB_BAR.CONTACTS',
    aria: 'SIDEBAR.CONTACTS',
  },
  {
    key: 'products',
    icon: 'i-ph-package',
    iconActive: 'i-ph-package-fill',
    label: 'MOBILE_TAB_BAR.PRODUCTS',
    aria: 'PRODUCTS.TITLE',
  },
];

/**
 * Emit `tabChange` and invoke the optional `onTabChange` prop callback.
 * @param {string} tab - The selected tab key.
 * @returns {void}
 */
const triggerTabChange = tab => {
  emit('tabChange', tab);
  props.onTabChange?.(tab);
};

/**
 * Emit `openNavigation` and invoke the optional `onOpenNavigation` callback.
 * @returns {void}
 */
const triggerOpenNavigation = () => {
  emit('openNavigation');
  props.onOpenNavigation?.();
};

/**
 * Route a tap to the navigation launcher or to a regular tab change.
 * @param {string} tab - The tapped tab key.
 * @returns {void}
 */
const handleAction = tab => {
  if (tab === 'navigation') {
    triggerOpenNavigation();
    return;
  }

  triggerTabChange(tab);
};

/**
 * Whether the given tab is the currently active one.
 * @param {string} tab - The tab key to test.
 * @returns {boolean}
 */
const isActive = tab => props.activeTab === tab;

/**
 * Icon glyph for a tab: the filled variant when active, the outline otherwise.
 * @param {{ key: string, icon: string, iconActive: string }} tab - The tab.
 * @returns {string}
 */
const tabIcon = tab => (isActive(tab.key) ? tab.iconActive : tab.icon);

/**
 * Tailwind classes for a tab button: a soft accent-tinted chip when active, a
 * subtle hover wash otherwise.
 * @param {string} tab - The tab key.
 * @returns {Array<string>}
 */
const buttonClasses = tab => [
  'group flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-[26px] px-2 py-1.5 transition-all duration-200 ease-out active:scale-[0.97]',
  isActive(tab) ? 'bg-[#ececec]' : 'hover:bg-n-alpha-1',
];

/**
 * Tailwind classes for the icon, tinted with the accent color when active.
 * @param {string} tab - The tab key.
 * @returns {string}
 */
const iconClasses = tab =>
  isActive(tab) ? 'text-[#7a2e8f]' : 'text-n-slate-12';

/**
 * Tailwind classes for the caption, accent-colored and bold when active.
 * @param {string} tab - The tab key.
 * @returns {string}
 */
const labelClasses = tab =>
  isActive(tab) ? 'text-[#7a2e8f] font-bold' : 'text-n-slate-12 font-medium';
</script>

<template>
  <div
    id="mobile-tab-bar"
    class="fixed inset-x-0 bottom-0 z-50 px-4 pb-3 md:hidden pointer-events-none"
    :style="safeAreaPaddingStyle"
  >
    <div
      class="mx-auto flex w-full max-w-md items-stretch justify-center gap-2.5"
    >
      <div
        class="pointer-events-auto flex h-16 flex-1 items-stretch gap-0.5 rounded-[32px] bg-white/60 dark:bg-[rgb(24_24_27_/_0.6)] p-1.5 outline outline-1 outline-n-weak backdrop-blur-2xl backdrop-saturate-150 shadow-[0_10px_30px_rgba(15,23,42,0.12)]"
      >
        <button
          v-for="tab in navTabs"
          :key="tab.key"
          type="button"
          :class="buttonClasses(tab.key)"
          :aria-label="t(tab.aria)"
          :aria-current="isActive(tab.key) ? 'page' : undefined"
          @click="handleAction(tab.key)"
        >
          <Icon
            :icon="tabIcon(tab)"
            class="size-6 transition-colors duration-200 ease-out"
            :class="iconClasses(tab.key)"
          />
          <span
            class="max-w-full truncate py-px text-[10px] leading-[1.35] transition-colors duration-200 ease-out"
            :class="labelClasses(tab.key)"
          >
            {{ t(tab.label) }}
          </span>
        </button>
      </div>

      <button
        type="button"
        class="pointer-events-auto flex size-16 shrink-0 items-center justify-center rounded-full bg-white/60 dark:bg-[rgb(24_24_27_/_0.6)] text-n-slate-12 outline outline-1 outline-n-weak backdrop-blur-2xl backdrop-saturate-150 shadow-[0_10px_30px_rgba(15,23,42,0.12)] transition-all duration-200 ease-out hover:bg-n-alpha-1 active:scale-[0.97]"
        :aria-label="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        @click="handleAction('search')"
      >
        <Icon icon="i-ph-magnifying-glass" class="size-7" />
      </button>
    </div>
  </div>
</template>
